import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiPhone, FiMail, FiClock, FiSend, FiCheckCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { contactAPI } from '../api';
import SEO from '../components/seo/SEO';
import { breadcrumb } from '../components/seo/jsonLd';

const HOURS = [
  { day:'Monday – Friday', hours:'9:00 AM – 8:00 PM' },
  { day:'Saturday', hours:'9:00 AM – 7:00 PM' },
  { day:'Sunday', hours:'10:00 AM – 5:00 PM' },
];

export default function Contact() {
  const [form, setForm] = useState({ name:'', email:'', phone:'', subject:'', message:'' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactAPI.send(form);
      setSent(true);
      toast.success('Message sent! We\'ll be in touch soon.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Salon Vora — Anna Nagar, Chennai"
        description="Visit, call or message Salon Vora in Anna Nagar, Chennai. Working hours, directions, phone +91 98765 43210, and online enquiry form."
        path="/contact"
        jsonLd={breadcrumb([
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' },
        ])}
      />
    <div className="min-h-screen bg-salon-cream">
      {/* Hero */}
      <section className="relative bg-hero-gradient py-32 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage:'radial-gradient(circle at 50% 50%, #8B1A4A 0%, transparent 70%)' }} />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-salon-primary/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-salon-secondary/15 blur-3xl pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.span initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
            className="section-tag mb-4 inline-block">Get in Touch</motion.span>
          <motion.h1 initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ delay:.1 }}
            className="font-display text-4xl md:text-5xl text-white mb-4">
            We'd Love to <span className="text-salon-secondary">Hear from You</span>
          </motion.h1>
        </div>
      </section>

      <section className="py-20 container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
          {/* Info */}
          <div>
            <h2 className="font-display text-3xl text-salon-dark mb-8">Visit Us</h2>
            <div className="space-y-6">
              {[
                { icon: FiMapPin, label:'Address', value:'42 Velvet Lane, Fashion District, Mumbai 400001' },
                { icon: FiPhone, label:'Phone', value:'+91 98765 43210' },
                { icon: FiMail, label:'Email', value:'hello@salonvora.com' },
              ].map(item => (
                <div key={item.label} className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-salon-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-salon-primary text-xl" />
                  </div>
                  <div>
                    <div className="text-sm text-salon-muted mb-0.5">{item.label}</div>
                    <div className="text-salon-dark font-medium">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Hours */}
            <div className="mt-10">
              <div className="flex items-center gap-3 mb-5">
                <FiClock className="text-salon-primary text-xl" />
                <h3 className="font-display text-xl text-salon-dark">Working Hours</h3>
              </div>
              <div className="space-y-3">
                {HOURS.map(h => (
                  <div key={h.day} className="flex justify-between items-center py-3 border-b border-salon-cream-dark last:border-0">
                    <span className="text-salon-dark font-medium">{h.day}</span>
                    <span className="text-salon-primary font-semibold">{h.hours}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="mt-10 rounded-2xl overflow-hidden h-48 bg-gradient-to-br from-salon-cream-dark to-salon-cream flex items-center justify-center border border-salon-cream-dark">
              <div className="text-center">
                <FiMapPin className="text-salon-primary text-3xl mx-auto mb-2" />
                <p className="text-salon-muted text-sm">Map integration available</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="card p-8">
            {sent ? (
              <motion.div initial={{ opacity:0, scale:.9 }} animate={{ opacity:1, scale:1 }}
                className="flex flex-col items-center justify-center h-full py-10 text-center">
                <FiCheckCircle className="text-green-500 text-6xl mb-6" />
                <h3 className="font-display text-2xl text-salon-dark mb-3">Message Received!</h3>
                <p className="text-salon-muted mb-6">Thank you for reaching out. Our team will contact you within 24 hours.</p>
                <button onClick={() => { setSent(false); setForm({ name:'', email:'', phone:'', subject:'', message:'' }); }}
                  className="btn-primary">Send Another Message</button>
              </motion.div>
            ) : (
              <>
                <h2 className="font-display text-2xl text-salon-dark mb-6">Send a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-salon-dark mb-1">Your Name *</label>
                      <input value={form.name} onChange={e => setForm({...form, name:e.target.value})}
                        required placeholder="Jane Doe" className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-salon-dark mb-1">Phone</label>
                      <input value={form.phone} onChange={e => setForm({...form, phone:e.target.value})}
                        placeholder="+91 00000 00000" className="input-field" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-salon-dark mb-1">Email Address *</label>
                    <input type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})}
                      required placeholder="jane@example.com" className="input-field" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-salon-dark mb-1">Subject</label>
                    <input value={form.subject} onChange={e => setForm({...form, subject:e.target.value})}
                      placeholder="How can we help?" className="input-field" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-salon-dark mb-1">Message *</label>
                    <textarea rows={5} value={form.message} onChange={e => setForm({...form, message:e.target.value})}
                      required placeholder="Tell us more..." className="input-field resize-none" />
                  </div>
                  <button type="submit" disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-2">
                    {loading ? 'Sending...' : <><FiSend /> Send Message</>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
